import { type FC } from 'react'
import classNames from 'classnames'
import { Link } from 'react-router-dom'
import { citationSourceLabel, isMeetingSource } from '../../../common/citationSource'
import { isLiveSession } from '../../../common/session.util'
import { useAccountTimezone } from '../../../common/useAccountTimezone'
import { parseMetadata } from '../../../common/util'
import { Tooltip } from '../../../components/Tooltip/Tooltip'
import { type Citation, type MeetingCitationSource } from '../../../models/Citation'
import { useGetRoomSessionQuery } from '../../../store/media/media'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { meetingPrepPath, pastMeetingPath } from '../../Scheduling/shared/bookingNavigation'
import { formatAsOf } from '../domain/asOf'
import { useWizardFlow } from '../hooks/WizardFlowContext'
import styles from '../AccountOpeningWizard.module.scss'

const MEETING_FALLBACK_TITLE = 'Meeting'

const MeetingSource: FC<{ source: MeetingCitationSource }> = ({ source }) => {
  const { ctx } = useWizardFlow()
  const timeZone = useAccountTimezone()
  const { tracked } = useTracking()
  const hasIds = !!source.room_id && !!source.session_id
  const { data } = useGetRoomSessionQuery({ accountId: ctx.accountId, roomId: source.room_id, sessionId: source.session_id }, { skip: !hasIds })
  const session = data?.data
  const when = session ? formatAsOf(session.startedAt, timeZone) : undefined
  const title = parseMetadata(session?.metadata)?.external?.title || MEETING_FALLBACK_TITLE
  const href = session && isLiveSession(session) ? meetingPrepPath(source.room_id, source.session_id) : pastMeetingPath(source.room_id, source.session_id)
  const handleOpen = tracked(() => undefined, { eventName: TrackEvent.CITATION_OPENED, eventDetails: { surface: 'accountOpening' } })

  return (
    <>
      <span className='text-nowrap'>from Zocks Meeting</span>
      {when ? <span className='text-nowrap'>{when}</span> : null}
      {hasIds ? (
        <Link className={classNames('zocks-text-primary', styles.citationTitle)} to={href} target='_blank' rel='noreferrer' onClick={handleOpen}>
          {title}
        </Link>
      ) : (
        <span className={styles.citationTitle}>{title}</span>
      )}
    </>
  )
}

// A meeting line's link takes the focus that opens the tooltip; any other line has nothing focusable, so the line itself takes it.
const CitationLine: FC<{ citation: Citation }> = ({ citation }) => {
  const isMeeting = isMeetingSource(citation.source)
  return (
    <Tooltip tooltipContent={citation.description} placement='top'>
      <span
        className='paragraph-small zocks-text-gray-600 d-inline-flex flex-wrap justify-content-end align-items-center zocks-gap-0.25 text-end mw-100'
        tabIndex={isMeeting ? undefined : 0}
      >
        <i className='bi bi-stars' aria-hidden='true' />
        {isMeetingSource(citation.source) ? (
          <MeetingSource source={citation.source} />
        ) : (
          <span className='text-break'>from {citationSourceLabel(citation.source)}</span>
        )}
      </span>
    </Tooltip>
  )
}

interface FieldCitationProps {
  citations: Citation[]
}

// The provenance line under an AI-filled field: `✦ from Zocks Meeting 08/10/2026, 9:41 AM ET <meeting title>`.
export const FieldCitation: FC<FieldCitationProps> = ({ citations }) =>
  citations.length === 0 ? null : (
    <div className={classNames('d-flex flex-column align-items-end zocks-gap-0.25', styles.citations)} data-testid='field-citation'>
      {citations.map((citation, index) => (
        <CitationLine key={`${citation.id}-${index}`} citation={citation} />
      ))}
    </div>
  )
